/**
 * Lightweight event bus for sync lifecycle events.
 * Replaces the SyncProvider facade's .on('local-ready' | 'sync-complete') API.
 * Consumers subscribe here; YjsRegistry/SyncEngine fire here.
 */

type LocalReadyListener = (id: string) => void;
type SyncCompleteListener = (id: string) => void;
type AuthErrorListener = (message: string) => void;

/**
 * Payload of a `KIND_WORKSPACE_MEMBERSHIP_CHANGED` envelope. The server emits
 * one of three variants when an admin mutates a user's workspace state in real
 * time — see docs/4-engineering/architecture/sync-architecture.md.
 */
export type WorkspaceMembershipChange =
  | { change: 'removed' }
  | { change: 'role_changed'; newRole: string }
  | { change: 'default_permission_changed'; newDefaultPermission: string };

type WorkspaceMembershipChangedListener = (change: WorkspaceMembershipChange) => void;

const localReadyListeners = new Set<LocalReadyListener>();
const syncCompleteListeners = new Set<SyncCompleteListener>();
const authErrorListeners = new Set<AuthErrorListener>();
const workspaceMembershipChangedListeners = new Set<WorkspaceMembershipChangedListener>();

export function onLocalReady(fn: LocalReadyListener): () => void {
  localReadyListeners.add(fn);
  return () => localReadyListeners.delete(fn);
}

export function onSyncComplete(fn: SyncCompleteListener): () => void {
  syncCompleteListeners.add(fn);
  return () => syncCompleteListeners.delete(fn);
}

export function onAuthError(fn: AuthErrorListener): () => void {
  authErrorListeners.add(fn);
  return () => authErrorListeners.delete(fn);
}

/**
 * Subscribe to workspace-membership change pushes. Returns an unsubscribe fn.
 *
 * The listener receives one of three variants from the server (see
 * `WorkspaceMembershipChange`). Typical consumer: call
 * `useWorkspaceStore.refreshFromServer()`. For the `removed` variant, also
 * navigate away if the active workspace is the one that was just lost — the
 * store's refresh-handling falls back to the personal workspace, but UI
 * routes don't unmount on their own.
 */
export function onWorkspaceMembershipChanged(
  fn: WorkspaceMembershipChangedListener,
): () => void {
  workspaceMembershipChangedListeners.add(fn);
  return () => workspaceMembershipChangedListeners.delete(fn);
}

export function fireLocalReady(id: string): void {
  localReadyListeners.forEach((fn) => fn(id));
}

export function fireSyncComplete(id: string): void {
  syncCompleteListeners.forEach((fn) => fn(id));
}

export function fireAuthError(message: string): void {
  authErrorListeners.forEach((fn) => fn(message));
}

export function fireWorkspaceMembershipChanged(change: WorkspaceMembershipChange): void {
  workspaceMembershipChangedListeners.forEach((fn) => fn(change));
}
